import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/context/auth";
import { createSignupFunnelTracker } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, RefreshCw, Eye, EyeOff, Copy, Check, ArrowLeft, ArrowRight, Mail, Shirt, ShoppingBasket, UtensilsCrossed, Scissors, Store, GraduationCap } from "lucide-react";
import { Link } from "wouter";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

const INDUSTRY_SECTORS = [
  {
    type: "school_campus",
    label: "School & Campus",
    description: "Campus-shop sales and stock, plus student fees and term balances.",
    Icon: GraduationCap,
    accent: "from-blue-500 to-indigo-600",
  },
  {
    type: "fashion",
    label: "Fashion & Boutique",
    description: "Sizes, colours, collections, variants, and customer returns.",
    Icon: Shirt,
    accent: "from-pink-500 to-rose-600",
  },
  {
    type: "supermarket",
    label: "Supermarket & Grocery",
    description: "Fast-moving stock, suppliers, expiry tracking, and checkout.",
    Icon: ShoppingBasket,
    accent: "from-emerald-500 to-teal-600",
  },
  {
    type: "food",
    label: "Food & Buka",
    description: "Meals, ingredients, portions, daily sales, and expenses.",
    Icon: UtensilsCrossed,
    accent: "from-orange-500 to-red-600",
  },
  {
    type: "salon",
    label: "Salon & Beauty",
    description: "Services, appointments, clients, products, and staff.",
    Icon: Scissors,
    accent: "from-fuchsia-500 to-purple-600",
  },
  {
    type: "general_retail",
    label: "General Retail",
    description: "Flexible inventory, sales, purchases, customers, and reports.",
    Icon: Store,
    accent: "from-blue-500 to-indigo-600",
  },
] as const;

type IndustryType = (typeof INDUSTRY_SECTORS)[number]["type"];

function generatePassword(length = 12): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%&";
  const all = upper + lower + digits + special;
  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    special[Math.floor(Math.random() * special.length)],
  ];
  const rest = Array.from({ length: length - 4 }, () => all[Math.floor(Math.random() * all.length)]);
  return [...required, ...rest].sort(() => Math.random() - 0.5).join("");
}

function PasswordStrength({ password }: { password: string }) {
  const checks = [
    { label: "8+ chars", ok: password.length >= 8 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Number", ok: /\d/.test(password) },
    { label: "Special", ok: /[^a-zA-Z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const color = score <= 1 ? "bg-destructive" : score === 2 ? "bg-amber-500" : score === 3 ? "bg-yellow-400" : "bg-emerald-500";
  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className={`h-1 flex-1 rounded-full ${i <= score ? color : "bg-muted"}`} />
        ))}
      </div>
      <div className="flex gap-3">
        {checks.map(c => (
          <span key={c.label} className={`text-xs ${c.ok ? "text-emerald-600" : "text-muted-foreground"}`}>
            {c.ok ? "✓" : "○"} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Signup() {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const [signupFunnel] = useState(createSignupFunnelTracker);
  useEffect(() => { signupFunnel.start(); }, [signupFunnel]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [industryType, setIndustryType] = useState<IndustryType | "">("");
  const refFromUrl = new URLSearchParams(window.location.search).get("ref") ?? "";
  const [form, setForm] = useState({ storeName: "", name: "", email: "", phone: "", password: "", confirmPassword: "", referralCode: refFromUrl.toUpperCase() });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  // OTP state
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [emailProviderBlocked, setEmailProviderBlocked] = useState(false);
  const [otpDeliveryNotice, setOtpDeliveryNotice] = useState("");
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const verifyingRef = useRef(false);

  const handleGenerate = () => {
    const pwd = generatePassword(14);
    setForm(p => ({ ...p, password: pwd, confirmPassword: pwd }));
    setShowPassword(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(form.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAccountContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) { setError("Passwords do not match"); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    signupFunnel.reach("industry");
    setStep(2);
  };

  // Step 2 → Step 3: send OTP after the user chooses a sector.
  const handleSendOtp = async () => {
    setError("");
    if (!industryType) {
      setError("Please select an industry sector.");
      return;
    }
    setOtpSending(true);
    try {
      const res = await fetch(`${BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, name: form.name }),
      });
      const data = await res.json();
      if (!res.ok) {
        setEmailProviderBlocked(data.code === "EMAIL_PROVIDER_BLOCKED");
        setError(data.error || "Could not send code");
        return;
      }
      setEmailProviderBlocked(false);
      setOtpDeliveryNotice("Code sent. It may take up to 2 minutes. Check Spam, Promotions, or All Mail and search for “RetailPulse verification code”.");
      setOtp(["", "", "", "", "", ""]);
      signupFunnel.reach("email_verification");
      setStep(3);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpInput = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
    if (!digit && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setOtpSending(true);
    try {
      const res = await fetch(`${BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, name: form.name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Failed to resend code. Please try again.");
        return;
      }
      setOtp(["", "", "", "", "", ""]);
      setOtpDeliveryNotice("A new code was sent. Only the newest code will work. Check Spam, Promotions, or All Mail if it is not in your inbox.");
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  // Step 3: verify OTP then create account
  const handleVerifyAndSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifyingRef.current) return;
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter the 6-digit code sent to your email."); return; }
    verifyingRef.current = true;
    setOtpVerifying(true);
    setError("");
    try {
      // Verify OTP first
      const verifyRes = await fetch(`${BASE}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, code }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) { setError(verifyData.error || "Verification failed"); return; }

      // Create account
      const res = await fetch(`${BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          storeName: form.storeName,
          name: form.name,
          email: form.email,
          phone: form.phone.trim() || undefined,
          password: form.password,
          industryType,
          referralCode: form.referralCode.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Signup failed"); return; }
      signupFunnel.complete(industryType || "unknown", Boolean(form.referralCode));
      await refetch();
      setLocation("/");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      verifyingRef.current = false;
      setOtpVerifying(false);
    }
  };

  const selectedSector = INDUSTRY_SECTORS.find(sector => sector.type === industryType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">

        {/* Step 1: Account credentials */}
        {step === 1 && (
          <div className="mx-auto max-w-lg">
            <div className="mb-7 text-center">
              <div className="mb-6 flex items-center justify-center gap-3">
                <img src={LOGO} alt="RetailPulsePOS" className="h-10 w-10 rounded-xl object-cover" />
                <span className="text-2xl font-bold text-white">RetailPulsePOS</span>
              </div>
              <p className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Step 1 of 3</p>
              <h1 className="text-3xl font-bold text-white">Create your account</h1>
              <p className="mt-2 text-slate-300">Tell us who you are and where your business operates.</p>
            </div>

            <Card className="border-white/10 bg-white text-slate-900 shadow-2xl">
              <CardHeader>
                <CardTitle>Account credentials</CardTitle>
                <CardDescription>All fields marked below are used to provision your store.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAccountContinue} className="space-y-4">
                  <div>
                    <label htmlFor="signup-name" className="text-sm font-medium">Full Name</label>
                    <Input id="signup-name" placeholder="John Doe" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required autoComplete="name" className="mt-1 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" />
                  </div>
                  <div>
                    <label htmlFor="signup-store" className="text-sm font-medium">Store Name</label>
                    <Input id="signup-store" placeholder="My Retail Store" value={form.storeName} onChange={e => setForm(p => ({ ...p, storeName: e.target.value }))} required autoComplete="organization" className="mt-1 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="signup-email" className="text-sm font-medium">Email</label>
                      <Input id="signup-email" type="email" placeholder="you@yourstore.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required autoComplete="email" className="mt-1 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" />
                    </div>
                    <div>
                      <label htmlFor="signup-phone" className="text-sm font-medium">Phone Number</label>
                      <Input id="signup-phone" type="tel" placeholder="+234 800 000 0000" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required autoComplete="tel" className="mt-1 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label htmlFor="signup-password" className="text-sm font-medium">Password</label>
                      <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 text-xs text-primary" onClick={handleGenerate}>
                        <RefreshCw className="h-3 w-3" /> Generate
                      </Button>
                    </div>
                    <div className="relative">
                      <Input id="signup-password" type={showPassword ? "text" : "password"} placeholder="Minimum 8 characters" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required autoComplete="new-password" className="border-slate-300 bg-white pr-20 font-mono text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" />
                      <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
                        {form.password && (
                          <button type="button" aria-label="Copy password" onClick={handleCopy} className="rounded p-1 text-muted-foreground hover:text-foreground">
                            {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        )}
                        <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(v => !v)} className="rounded p-1 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>
                    {form.password && <PasswordStrength password={form.password} />}
                  </div>
                  <div>
                    <label htmlFor="signup-confirm-password" className="text-sm font-medium">Confirm Password</label>
                    <Input id="signup-confirm-password" type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} required autoComplete="new-password" className="mt-1 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" />
                  </div>
                  {form.referralCode && (
                    <div>
                      <label htmlFor="signup-referral" className="text-sm font-medium">Referral Code</label>
                      <Input id="signup-referral" value={form.referralCode} onChange={e => setForm(p => ({ ...p, referralCode: e.target.value.toUpperCase() }))} className="mt-1 border-slate-300 bg-white font-mono uppercase tracking-widest text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500" maxLength={8} />
                    </div>
                  )}
                  {error && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="w-full gap-2">
                    Continue to industry <ArrowRight className="h-4 w-4" />
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Link href="/login" className="font-medium text-primary hover:underline">Sign in</Link>
                  </p>
                  <p className="text-center text-sm text-muted-foreground">
                    Testing with no active email?{" "}
                    <Link href="/demo" className="font-medium text-primary hover:underline">
                      Open an instant demo
                    </Link>
                    — no verification required.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Industry sector */}
        {step === 2 && (
          <div>
            <div className="mb-7 text-center">
              <div className="mb-6 flex items-center justify-center gap-3">
                <img src={LOGO} alt="RetailPulsePOS" className="h-10 w-10 rounded-xl object-cover" />
                <span className="text-2xl font-bold text-white">RetailPulsePOS</span>
              </div>
              <p className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Step 2 of 3</p>
              <h1 className="text-3xl font-bold text-white">Choose your industry sector</h1>
              <p className="mt-2 text-slate-300">We’ll tailor your dashboard, navigation, and inventory tools.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {INDUSTRY_SECTORS.map(sector => {
                const selected = industryType === sector.type;
                return (
                  <button
                    key={sector.type}
                    type="button"
                    onClick={() => { setIndustryType(sector.type); setError(""); }}
                    className={`relative overflow-hidden rounded-2xl border-2 p-4 text-left transition-all ${
                      selected
                        ? "border-cyan-300 bg-white/15 shadow-xl shadow-cyan-950/30"
                        : "border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10"
                    }`}
                  >
                    {selected && <CheckCircle className="absolute right-3 top-3 h-5 w-5 text-cyan-300" />}
                    <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${sector.accent}`}>
                      <sector.Icon className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="pr-5 text-base font-bold leading-snug text-white">{sector.label}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-slate-300">{sector.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="mx-auto mt-6 flex max-w-2xl flex-col items-center justify-between gap-3 rounded-2xl border border-cyan-300/20 bg-cyan-400/10 px-5 py-4 text-center sm:flex-row sm:text-left">
              <div>
                <p className="font-semibold text-cyan-100">Just exploring RetailPulsePOS?</p>
                <p className="mt-1 text-sm text-cyan-100/70">Use an instant demo account with no email verification required.</p>
              </div>
              <Link href="/demo" className="shrink-0 rounded-lg border border-cyan-300/40 px-4 py-2 text-sm font-semibold text-cyan-100 transition-colors hover:bg-cyan-300/15">
                Open instant demos
              </Link>
            </div>

            {error && <p role="alert" className="mx-auto mt-5 max-w-lg rounded-md bg-red-500/15 px-3 py-2 text-center text-sm text-red-200">{error}</p>}
            {emailProviderBlocked && (
              <p className="mx-auto mt-3 max-w-lg text-center text-sm text-cyan-100/80">
                Need to test the product now?{" "}
                <Link href="/demo" className="font-semibold text-cyan-200 underline underline-offset-2 hover:text-white">
                  Open an instant demo without email verification
                </Link>
                .
              </p>
            )}

            <div className="mt-7 flex flex-col-reverse items-center justify-center gap-3 sm:flex-row">
              <Button type="button" variant="ghost" className="gap-2 text-slate-300 hover:text-white" onClick={() => { setStep(1); setError(""); }}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button type="button" size="lg" className="min-w-56 gap-2" disabled={!industryType || otpSending} onClick={handleSendOtp}>
                {otpSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {otpSending ? "Sending code..." : "Continue & verify email"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Verify and provision the store */}
        {step === 3 && (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <img src={LOGO} alt="RetailPulsePOS" className="w-10 h-10 rounded-xl object-cover" />
                <span className="text-2xl font-bold text-white">RetailPulsePOS</span>
              </div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">Step 3 of 3</p>
              <div className="w-16 h-16 rounded-full bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-indigo-300" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verify & finish setup</h2>
              <p className="text-slate-400">
                We sent a 6-digit code to <strong className="text-white">{form.email}</strong>.<br />
                Enter it below to provision your {selectedSector?.label ?? "retail"} dashboard.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Enter verification code</CardTitle>
                <CardDescription>The code expires in 10 minutes</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerifyAndSignup} className="space-y-5">
                  {/* 6-digit OTP boxes */}
                  <div className="flex gap-3 justify-center" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={el => { otpRefs.current[i] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleOtpInput(i, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(i, e)}
                        className="w-11 h-14 text-center text-2xl font-bold border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono caret-transparent"
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>

                  {error && <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md text-center">{error}</p>}
                  {otpDeliveryNotice && !error && (
                    <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 px-3 py-2 rounded-md text-center">
                      {otpDeliveryNotice}
                    </p>
                  )}

                  <Button type="submit" className="w-full" disabled={otpVerifying || otp.join("").length < 6}>
                    {otpVerifying && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {otpVerifying ? "Provisioning your dashboard..." : "Verify & Open Dashboard"}
                  </Button>

                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpSending}
                      className="text-sm text-primary hover:underline disabled:opacity-50"
                    >
                      {otpSending ? "Sending..." : "Resend code"}
                    </button>
                    <br />
                    <button
                      type="button"
                      onClick={() => { setStep(2); setError(""); setOtp(["", "", "", "", "", ""]); }}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      ← Change industry sector
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
